// auth.guard.ts
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthService } from './auth.service';
import { catchError, firstValueFrom, map, Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  constructor(private authService: AuthService, private router: Router) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    // Define rutas que NO necesitan autenticación
    const publicRoutes = ['login', 'register', 'restart-pass'];
    
    // Extrae la primera parte de la ruta sin la barra inicial
    const requestedPath = state.url.split('/')[1]?.split('?')[0] || '';
    
    const isPublicRoute = publicRoutes.includes(requestedPath);
    
    // 1. Si es ruta pública, permitir acceso independientemente del estado de login
    if (isPublicRoute) {
      return of(true);
    }
    
    // Para rutas protegidas, verificamos la existencia de cookies primero
    const hasTokenCookie = this.authService.checkCookieExists('token');
    const hasRefreshTokenCookie = this.authService.checkCookieExists('refresh_token');
    
    // Si hay user en sessionStorage, permitir el acceso inmediatamente
    if (sessionStorage.getItem('user')) {
      return of(true);
    }
    
    // Si hay cookies pero no hay user (posible escenario entre apps), intentamos inicializar la sesión
    if (hasTokenCookie && hasRefreshTokenCookie) {
      // Guardamos la URL para redirigir después
      sessionStorage.setItem('redirectUrl', state.url);
      
      // Intentamos restaurar la sesión y devolvemos la respuesta como un Observable<boolean>
      return this.authService.restoreSessionFromCookies().pipe(
        map(success => {
          if (success) {
            return true; // Permitir acceso
          } else {
            this.router.navigate(['/login']);
            return false; // Denegar acceso
          }
        }),
        catchError(() => {
          this.router.navigate(['/login']);
          return of(false);
        })
      );
    }
    
    // Si no hay cookies ni user, redirigir a login
    sessionStorage.setItem('redirectUrl', state.url);
    this.router.navigate(['/login']);
    return of(false);
  }
}
